<script>
import { defineComponent as _defineComponent } from "vue";
import { unref as _unref, renderSlot as _renderSlot, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue";
import { computed, inject, ref, toValue, watch } from "vue";
import { Primitive } from "@maas/vue-primitive";
import { useMenuState } from "../composables/private/useMenuState";
import { useMenuView } from "../composables/private/useMenuView";
import { useMenuItem } from "../composables/private/useMenuItem";
import { useMenuTrigger } from "../composables/private/useMenuTrigger";
import {
  MagicMenuInstanceId,
  MagicMenuViewId,
  MagicMenuItemId
} from "../symbols";
import { onKeyStroke } from "@vueuse/core";
export default /* @__PURE__ */ _defineComponent({
  __name: "MagicMenuTrigger",
  props: {
    instanceId: { type: String, required: false },
    viewId: { type: String, required: false },
    disabled: { type: Boolean, required: false },
    trigger: { type: Array, required: false },
    asChild: { type: Boolean, required: false }
  },
  setup(__props) {
    const elRef = ref(void 0);
    const injectedInstanceId = inject(MagicMenuInstanceId, void 0);
    const injectedViewId = inject(MagicMenuViewId, void 0);
    const itemId = inject(MagicMenuItemId, void 0);
    const mappedInstanceId = computed(() => __props.instanceId ?? injectedInstanceId);
    const mappedViewId = computed(() => __props.viewId ?? injectedViewId);
    if (!mappedInstanceId.value) {
      throw new Error(
        "MagicMenuRemote must be nested inside MagicMenuProvider or an instanceId must be provided"
      );
    }
    if (!mappedViewId.value) {
      throw new Error(
        "MagicMenuTrigger must be nested inside MagicMenuView or a viewId must be provided"
      );
    }
    const { getView, getRelativeViewIndex } = useMenuView(mappedInstanceId.value);
    const view = getView(mappedViewId.value);
    const viewIndex = getRelativeViewIndex(mappedViewId.value);
    const { initializeState } = useMenuState(mappedInstanceId.value);
    const state = initializeState();
    const { getItem } = useMenuItem({
      instanceId: mappedInstanceId.value,
      viewId: mappedViewId.value
    });
    const item = getItem(itemId ?? "");
    const mappedDisabled = computed(() => __props.disabled ?? item?.disabled ?? false);
    const mappedTrigger = computed(() => {
      if (__props.trigger?.length) {
        return __props.trigger;
      }
      switch (state.options.mode) {
        case "menubar":
          return view?.parent.item ? ["mouseenter", "click"] : state.active ? ["mouseenter", "click"] : ["click"];
        case "dropdown":
          return view?.parent.item ? ["mouseenter", "click"] : ["click"];
        case "context":
          return view?.parent.item ? ["mouseenter", "click"] : ["right-click"];
        case "navigation":
          return ["mouseenter"];
        default:
          return [];
      }
    });
    const mappedTabindex = computed(() => {
      if (viewIndex === 0 && state.options.mode !== "context" && !itemId) {
        return 0;
      } else {
        return void 0;
      }
    });
    const { onMouseenter, onClick, onEnter } = useMenuTrigger({
      instanceId: mappedInstanceId.value,
      viewId: mappedViewId.value,
      itemId,
      mappedDisabled,
      mappedTrigger,
      elRef
    });
    watch(
      () => view?.active,
      async (value) => {
        if (value) {
          await new Promise((resolve) => requestAnimationFrame(resolve));
          toValue(elRef)?.$el?.blur();
        }
      }
    );
    onKeyStroke("Enter", onEnter);
    return (_ctx, _cache) => {
      return _openBlock(), _createBlock(_unref(Primitive), {
        ref_key: "elRef",
        ref: elRef,
        "data-id": `${mappedViewId.value}-trigger`,
        "data-active": _unref(view)?.active,
        "data-disabled": mappedDisabled.value,
        tabindex: mappedTabindex.value,
        "as-child": _ctx.asChild,
        class: "magic-menu-trigger",
        onClick: _unref(onClick),
        onContextmenu: _unref(onClick),
        onMouseenter: _unref(onMouseenter)
      }, {
        default: _withCtx(() => [
          _renderSlot(_ctx.$slots, "default", {
            viewActive: _unref(view)?.active,
            triggerDisabled: mappedDisabled.value
          })
        ]),
        _: 3
        /* FORWARDED */
      }, 8, ["data-id", "data-active", "data-disabled", "tabindex", "as-child", "onClick", "onContextmenu", "onMouseenter"]);
    };
  }
});
</script>

<style>
.magic-menu-trigger {
  cursor: var(--magic-menu-trigger-cursor, pointer);
}

.magic-menu-trigger[data-disabled='true'] {
  pointer-events: none;
}
</style>
